import { useState, useEffect, useRef } from 'react';
import './ItemSuggestions.css';
import { useItemSuggestions } from '../hooks/useItemSuggestions';

interface ItemSuggestionsProps {
  scenarioId: string;
  searchQuery: string;
  onSelect: (itemName: string) => void;
  isVisible: boolean;
  excludeItems?: string[];
  showSelectedIndex?: boolean;
  position?: 'below' | 'above';
}

export function ItemSuggestions({ scenarioId, searchQuery, onSelect, isVisible, excludeItems = [], showSelectedIndex = false, position = 'below' }: ItemSuggestionsProps) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { suggestions, loading } = useItemSuggestions(scenarioId, searchQuery, isVisible, excludeItems);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    // Reset selection when suggestion set changes.
    setSelectedIndex(-1);
  }, [scenarioId, searchQuery, isVisible, suggestions.length]);

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isVisible || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (position === 'above') {
          // When dropdown is above, down arrow moves down the list (increases index)
          setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else {
          // When dropdown is below, down arrow moves down the list (increases index)
          setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (position === 'above') {
          // When dropdown is above, up arrow moves up the list (decreases index)
          // On first press (prev === -1), select the bottom item (closest to input)
          setSelectedIndex((prev) => {
            if (prev === -1) return suggestions.length - 1;
            return Math.max(prev - 1, 0);
          });
        } else {
          // When dropdown is below, up arrow moves up the list (decreases index)
          setSelectedIndex((prev) => Math.max(prev - 1, -1));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          onSelect(suggestions[selectedIndex].name);
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, suggestions, selectedIndex, position]);

  if (!isVisible || suggestions.length === 0) {
    return null;
  }

  return (
    <div className={`item-suggestions ${position === 'above' ? 'position-above' : 'position-below'}`}>
      {loading && <div className="suggestions-loading">Loading suggestions...</div>}
      {!loading && suggestions.length > 0 && (
        <ul ref={listRef} className="suggestions-list">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.name}
              className={`suggestion-item ${showSelectedIndex && index === selectedIndex ? 'selected' : ''}`}
              onClick={() => onSelect(suggestion.name)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="suggestion-content">
                <span className="suggestion-name">
                  {suggestion.quantity > 1 ? `${suggestion.quantity}x ` : ''}{suggestion.name}
                </span>
                <span className="suggestion-meta">
                  <span className="last-used" title="Last used">
                    {formatRelativeTime(suggestion.lastUsedAt)}
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!loading && suggestions.length === 0 && searchQuery && (
        <div className="no-suggestions">No suggestions found</div>
      )}
    </div>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
